import { Route, Switch, Redirect } from "react-router-dom";
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createLightTheme, createDarkTheme } from './theme/theme';
import { ThemeContextProvider } from './contextApi/themeContext';
import ThemeCtx from './contextApi/themeContext';
import { LanguageContextProvider } from './contextApi/languageContext';
import LanguageCtx from './contextApi/languageContext';
import { PermissionProvider } from './contextApi/PermissionContext';
import { BranchProvider } from './contextApi/BranchContext';
import { ToolsProvider } from './contextApi/toolsContext';

import Main from './components/main/main';
import Mis from './components/mis/mis';
import LogIn from './components/authAndConnections/logIn';
import AuthContext from './components/authAndConnections/auth';
import { useContext, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import AxiosGlobal from './components/authAndConnections/axiosGlobalUrl';
import { fetchData, getAllTags, getContacts, getFilter, userProfileData, fetchUserDirectory } from './store/store';
import { useDispatch, useSelector } from 'react-redux';
import ShortLinkResolver from './components/main/shortLinkResolver';
import PublicLinkPage from './components/digitalMarketing/publicLinkPage';
import RestrictedAccessScreen from './components/main/restrictedAccessScreen';
import { useHistory, useLocation, Link } from "react-router-dom";
import SnackBar from './tools/navs/snackBar';
import useEnterAdvancesFields from './tools/enterAdvancesFields';
import useScriptFonts from './tools/scriptFonts';
import GhostBanner from './components/main/ghostBanner';
import PwaInstallPrompt from './tools/navs/pwaInstallPrompt';
import EnableNotificationsPrompt from './tools/navs/enableNotificationsPrompt';
import FloatingTools from './tools/floatingTools/floatingTools';
import { pushSupported, subscribeToPush } from './tools/pushNotifications';

// Inner component so it can consume ThemeCtx after the provider mounts
const ThemedApp = () => {
  const { themeMode } = useContext(ThemeCtx);
  const { language, isRtl } = useContext(LanguageCtx);
  const authCtx = useContext(AuthContext);
  const [notifs, setNotifs] = useState('');
  const [count, setCount] = useState(0);
  const axiosGlobal = useContext(AxiosGlobal);
  const dispatch = useDispatch();
  const refresh = useSelector((state) => state.refresh);
  const history = useHistory();
  const misRefresh = useSelector((state) => state.misRefresh);
  const refreshTag = useSelector((state) => state.refreshTag);
  const userProfileRefresh = useSelector((state) => state.userProfileRefresh);

  // Memoized so unrelated re-renders (refresh/misRefresh/etc.) don't rebuild
  // the MUI theme object and cascade a re-render through every consumer.
  const muiTheme = useMemo(() => {
    const direction = isRtl ? 'rtl' : 'ltr';
    return themeMode === 'light' ? createLightTheme(direction, language) : createDarkTheme(direction, language);
  }, [themeMode, isRtl, language]);

  // CSS reads the theme off <html> (the alternating-row shade, see index.scss); set before paint so a dark screen never flashes light
  useLayoutEffect(() => { document.documentElement.setAttribute('data-xms-theme', themeMode); }, [themeMode]);

  if (localStorage.getItem('accessToken') === 'undefined') {
    localStorage.removeItem('accessToken');
  }

  useEffect(() => {}, [refresh]);

  useEffect(() => {
    dispatch(fetchData({ authCtx, axiosGlobal }));
  }, [refresh, refreshTag]);

  useEffect(() => {
    dispatch(getContacts({ authCtx, axiosGlobal }));
  }, []);

  useEffect(() => {
    if (authCtx.isLoggedIn !== true) return;
    dispatch(fetchUserDirectory({ authCtx, axiosGlobal }));
  }, [authCtx.isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(getFilter({ authCtx, axiosGlobal }));
  }, [misRefresh]);

  useEffect(() => {
    dispatch(getAllTags({ authCtx, axiosGlobal }));
  }, [refreshTag]);

  useEffect(() => {
    dispatch(userProfileData({ authCtx, axiosGlobal }));
  }, [userProfileRefresh]);

  // Keep the push subscription alive: if the user already granted permission,
  // (re)subscribe on every load and re-save it server-side. Subscriptions can
  // be dropped by the browser/OS, so re-registering here is what keeps push
  // arriving when the app/browser is closed — without it a stale/missing
  // subscription silently stops delivering.
  useEffect(() => {
    if (authCtx.isLoggedIn !== true) return;
    if (!pushSupported() || Notification.permission !== 'granted') return;
    subscribeToPush(authCtx, axiosGlobal).catch(() => {});
  }, [authCtx.isLoggedIn]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Service worker → app bridge: when a push notification is clicked and the app
  // is already open, the SW posts the target route here for smooth in-app
  // navigation (no full reload).
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMsg = (event) => {
      if (event.data?.type === 'notification-navigate' && event.data.url) {
        history.push(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [history]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <PermissionProvider>
      <BranchProvider>
      <ToolsProvider>
        <SnackBar />
        {authCtx.isLoggedIn === true && <GhostBanner />}
        {authCtx.isLoggedIn === true && <PwaInstallPrompt />}
        {authCtx.isLoggedIn === true && <EnableNotificationsPrompt />}
        {/* The calendar (F5) and the calculator (F6), floating above every screen - see tools/floatingTools/ */}
        {authCtx.isLoggedIn === true && <FloatingTools />}
        <Switch>
          {/* ── PUBLIC ROUTES MUST STAY AT THE TOP OF THIS SWITCH ──────────
              Every auth-gated block below renders a bare <Redirect to="/logIn" />
              when logged out, and a <Redirect> with no `from` prop matches EVERY
              location inside a <Switch>. So the first one wins and nothing after
              it is reachable while logged out. These two have to be matched
              before that happens — moving them back down silently breaks public
              access again (which is exactly what happened before). */}

          {/* Genuinely public, no login at all — a customer with no XMS
              account opens this. See publicLinkPage.js + the unauthenticated
              GET /digitalMarketing/public/link-pages/:code backend route. */}
          <Route path="/p/:code" exact>
            <PublicLinkPage />
          </Route>

          {/* Outside the auth gate on purpose — the resolver handles the
              logged-out case itself (redirect to /logIn + return here after). */}
          <Route path="/l/:code" exact>
            <ShortLinkResolver />
          </Route>

          <Route path="/logIn" exact>
            {authCtx.isLoggedIn === true ? <Redirect to="/" /> : <LogIn />}
          </Route>

          {authCtx.isLoggedIn === true ? (
            <Route exact path="/">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {authCtx.isLoggedIn === true ? (
            <Route path="/files">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {authCtx.isLoggedIn === true ? (
            <Route path="/mis">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {authCtx.isLoggedIn === true ? (
            <Route path="/crm">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {authCtx.isLoggedIn === true ? (
            <Route path="/inventory">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {authCtx.isLoggedIn === true ? (
            <Route exact path="/users">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {authCtx.isLoggedIn === true ? (
            <Route path="/digitalMarketing">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {/* Job Reports — login-only, no permission gate for "my reports"
              mode (same precedent as /myActivity below); "all reports" admin
              mode is gated inside the component by jobReports:viewAll. */}
          {authCtx.isLoggedIn === true ? (
            <Route path="/jobReports">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {authCtx.isLoggedIn === true ? (
            <Route path="/tutorials">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {/* Supply (Session 72) — permission-gated inside main.js like every other section. */}
          {authCtx.isLoggedIn === true ? (
            <Route path="/supply">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {/* Report & Info — permission-gated (info:view) inside main.js like every other section. */}
          {authCtx.isLoggedIn === true ? (
            <Route path="/info">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {/* Self-service Activity Log + Job Reports — login-only, deliberately
              NOT gated by users:view (see main.js's isMyActivity branch). */}
          {authCtx.isLoggedIn === true ? (
            <Route path="/myActivity">
              <Main />
            </Route>
          ) : (
            <Redirect to="/logIn" />
          )}

          {authCtx.isLoggedIn === true && (
            <Route path="/restricted" exact>
              <RestrictedAccessScreen />
            </Route>
          )}

        </Switch>
      </ToolsProvider>
      </BranchProvider>
      </PermissionProvider>
    </ThemeProvider>
  );
};

function App() {
  // Enter moves to the next field in every form, app-wide (tools/enterAdvancesFields.js).
  useEnterAdvancesFields();
  // Arabic text in Amiri and Persian text in B Nazanin, whatever language the app is in (tools/scriptFonts.js).
  useScriptFonts();

  // service worker reg — once on mount, not every render; logs the real reason
  // on failure (e.g. a server rewrite serving index.html for /serviceWorker.js
  // instead of the actual script — a MIME-type mismatch the browser rejects).
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/serviceWorker.js')
        .then(() => { console.log('service worker registered'); })
        .catch((err) => { console.error('service worker registration failed:', err); });
    }
  }, []);

  return (
    <LanguageContextProvider>
      <ThemeContextProvider>
        <ThemedApp />
      </ThemeContextProvider>
    </LanguageContextProvider>
  );
}

export default App;
